import { useState } from "react";
import DataTable from "@/components/DataTable";
import TradeToolShell, { type ToolCategory } from "@/components/TradeToolShell";
import { useHaptics } from "@/hooks/use-haptics";
import { OffsetCalculator, RollingOffsetCalculator, FrictionLossCalculator, EquivalentLengthCalculator, GpmSizingCalculator, MaxGpmChart } from "@/components/pipe/PipeCalculators";
import { DrainSizingCalculator, VentSizingCalculator, WaterSupplySizingCalculator } from "@/components/pipe/PlumbingCalculators";
import { PipeThermalExpansion, PipeInsulationCalc } from "@/components/pipe/PipeExpansionInsul";
import { FlangeTorqueCalculator } from "@/components/pipe/FlangeTorque";
import { PipeWeightCalculator } from "@/components/pipe/PipeWeight";
import { HangerLoadCalculator, TrapezeHangerCalculator } from "@/components/pipe/HangerTools";
import { SteamPipeSizingCalculator } from "@/components/pipe/SteamPipeSizing";
import { GasPipeSizingCalculator } from "@/components/pipe/GasPipeSizing";
import { PressureDropCalculator } from "@/components/pipe/PressureDropCalc";
import { PipeThreadReference } from "@/components/pipe/PipeThreadRef";
import { FactoredWeldInchCalc } from "@/components/pipe/FactoredWeldInch";
import { grooveWeldHeaders, grooveWeldRows } from "@/components/pipe/pipe-extras-data";
import TradeFormulas from "@/components/TradeFormulas";
import {
  steelHeaders, steelRows, copperHeaders, copperRows,
  pvcHeaders, pvcRows, cpvcHeaders, cpvcRows,
  ssHeaders, ssRows, ciHeaders, ciRows,
  supportHeaders, supportRows,
  fittingHeaders, fittingRows,
  flangeBoltHeaders, flangeBoltRows,
  gasketHeaders, gasketRows,
  flangeFaceHeaders, flangeFaceRows,
  boltTorqueHeaders, boltTorqueRows,
  valveCvHeaders, valveCvRows,
  threadTapHeaders, threadTapRows,
  steamTableHeaders, steamTableRows,
} from "@/components/pipe/pipe-reference-data";
import {
  fixtureUnitHeaders, fixtureUnitRows,
  drainSizingHeaders, drainSizingRows,
  ventSizingHeaders, ventSizingRows,
} from "@/components/pipe/plumbing-reference-data";

const PIPE_CATEGORIES: ToolCategory[] = [
  {
    id: "sizing", label: "Sizing", color: "bg-primary",
    tools: [
      { value: "steel", label: "Steel" }, { value: "copper", label: "Copper" },
      { value: "pvc", label: "PVC" }, { value: "cpvc", label: "CPVC" },
      { value: "ss", label: "SS" }, { value: "ci", label: "Cast Iron" },
    ],
  },
  {
    id: "fittings", label: "Fittings", color: "bg-copper",
    tools: [
      { value: "fittings", label: "Dims" },
      { value: "flangeref", label: "Flanges" },
      { value: "torque", label: "Torque" },
      { value: "valvecv", label: "Valve Cv" },
      { value: "supports", label: "Supports" },
    ],
  },
  {
    id: "calcs", label: "Calcs", color: "bg-safety",
    tools: [
      { value: "offset", label: "Offset" }, { value: "rolling", label: "Rolling" },
      { value: "friction", label: "Friction" }, { value: "eqlength", label: "Eq. Len" },
      { value: "pressuredrop", label: "ΔP Run" },
      { value: "gpmsizing", label: "GPM Size" }, { value: "maxgpm", label: "Max GPM" },
      { value: "steamsizing", label: "Steam" }, { value: "gassizing", label: "Gas" },
      { value: "expansion", label: "Expand" }, { value: "insulation", label: "Insul." },
      { value: "pipeweight", label: "Weight" }, { value: "hangerload", label: "Hanger" }, { value: "trapeze", label: "Trapeze" },
    ],
  },
  {
    id: "plumbing", label: "Plumbing", color: "bg-pipe-green",
    tools: [
      { value: "fixtures", label: "Fixtures" },
      { value: "drains", label: "Drains" }, { value: "vents", label: "Vents" },
      { value: "supply", label: "Supply" },
      { value: "draintbl", label: "Drain Tbl" }, { value: "venttbl", label: "Vent Tbl" },
    ],
  },
  {
    id: "reference", label: "Reference", color: "bg-conduit",
    tools: [
      { value: "threadref", label: "NPT Ref" }, { value: "threadtap", label: "Thread/Tap" },
      { value: "steam", label: "Steam Tbl" },
      { value: "grooveweld", label: "Groove/Weld" },
      { value: "fdi", label: "FDI Calc" },
      { value: "formulas", label: "Formulas" },
    ],
  },
];

// Legacy `?tool=` values kept working after the flange/torque consolidation
const TOOL_ALIASES: Record<string, string> = {
  flanges: "flangeref",
  gaskets: "flangeref",
  flangeface: "flangeref",
  flangetorque: "torque",
  bolttorque: "torque",
};

const PipeSizing = () => {
  const { lightTap } = useHaptics();
  const [flangeView, setFlangeView] = useState<"bolts" | "gaskets" | "face">("bolts");
  const [torqueView, setTorqueView] = useState<"flange" | "bolt">("flange");

  const renderTool = (activeTool: string) => {
    switch (activeTool) {
      case "steel": return <div className="-mx-4"><DataTable headers={steelHeaders} rows={steelRows} searchable searchPlaceholder="Search steel pipe sizes…" favoritesConfig={{ type: "pipe", category: "🔧 Steel Pipe", makeId: (row) => `pipe-steel-${row[0]}`, makeName: (row) => `Steel ${row[0]}`, makeDetail: (row) => `OD ${row[1]}" · ID ${row[2]}" · ${row[4]} lb/ft` }} /></div>;
      case "copper": return <div className="-mx-4"><DataTable headers={copperHeaders} rows={copperRows} searchable searchPlaceholder="Search copper pipe sizes…" favoritesConfig={{ type: "pipe", category: "🔧 Copper Pipe", makeId: (row) => `pipe-copper-${row[0]}-${row[1]}`, makeName: (row) => `Copper ${row[0]} ${row[1]}`, makeDetail: (row) => `OD ${row[2]}" · ID ${row[3]}" · Wall ${row[4]}"` }} /></div>;
      case "pvc": return <div className="-mx-4"><DataTable headers={pvcHeaders} rows={pvcRows} searchable searchPlaceholder="Search PVC pipe sizes…" favoritesConfig={{ type: "pipe", category: "🔧 PVC Pipe", makeId: (row) => `pipe-pvc-${row[0]}-${row[1]}`, makeName: (row) => `PVC Sch${row[0]} ${row[1]}`, makeDetail: (row) => `OD ${row[2]}" · ID ${row[3]}" · ${row[5]} PSI` }} /></div>;
      case "cpvc": return <div className="-mx-4"><DataTable headers={cpvcHeaders} rows={cpvcRows} searchable searchPlaceholder="Search CPVC pipe sizes…" favoritesConfig={{ type: "pipe", category: "🔧 CPVC Pipe", makeId: (row) => `pipe-cpvc-${row[0]}`, makeName: (row) => `CPVC ${row[0]}`, makeDetail: (row) => `OD ${row[1]}" · ID ${row[2]}" · ${row[4]} PSI @180°F` }} /></div>;
      case "ss": return <div className="-mx-4"><DataTable headers={ssHeaders} rows={ssRows} searchable searchPlaceholder="Search stainless pipe sizes…" favoritesConfig={{ type: "pipe", category: "🔧 Stainless Steel", makeId: (row) => `pipe-ss-${row[0]}-${row[1]}`, makeName: (row) => `SS ${row[0]} ${row[1]}`, makeDetail: (row) => `OD ${row[2]}" · ID ${row[3]}" · ${row[5]} lb/ft` }} /></div>;
      case "ci": return <div className="-mx-4"><DataTable headers={ciHeaders} rows={ciRows} searchable searchPlaceholder="Search cast iron pipe sizes…" favoritesConfig={{ type: "pipe", category: "🔧 Cast Iron", makeId: (row) => `pipe-ci-${row[0]}-${row[1]}`, makeName: (row) => `CI ${row[0]} ${row[1]}`, makeDetail: (row) => `OD ${row[2]}" · ID ${row[3]}" · ${row[5]} lb/ft` }} /></div>;
      case "supports": return <div className="-mx-4"><DataTable headers={supportHeaders} rows={supportRows} searchable searchPlaceholder="Search support spacing…" favoritesConfig={{ type: "pipe", category: "📏 Support Spacing", makeId: (row) => `support-${row[0]}`, makeName: (row) => `Support ${row[0]}`, makeDetail: (row) => `Steel ${row[1]}' · Cu ${row[2]}' · PVC ${row[3]}'` }} /></div>;
      case "fittings": return <div className="-mx-4"><DataTable headers={fittingHeaders} rows={fittingRows} searchable searchPlaceholder="Search fitting dimensions…" favoritesConfig={{ type: "pipe", category: "🔩 Fittings", makeId: (row) => `fitting-${row[0]}`, makeName: (row) => `Fitting ${row[0]}`, makeDetail: (row) => `90° ${row[1]}" · 45° ${row[2]}" · Tee ${row[3]}"` }} /></div>;
      case "flangeref": return (
        <div>
          <div className="flex gap-1 bg-secondary/50 rounded-lg p-0.5 mb-3">
            {(["bolts", "gaskets", "face"] as const).map(v => (
              <button key={v} onClick={() => { lightTap(); setFlangeView(v); }}
                className={`flex-1 py-1.5 px-2 rounded-md text-xs font-medium transition-all ${flangeView === v ? "bg-card text-foreground shadow-sm border border-border" : "text-muted-foreground"}`}>
                {v === "bolts" ? "Bolt Patterns" : v === "gaskets" ? "Gaskets" : "Face/Finish"}
              </button>
            ))}
          </div>
          <div className="-mx-4">
            {flangeView === "bolts" && <DataTable headers={flangeBoltHeaders} rows={flangeBoltRows} searchable searchPlaceholder="Search flange bolts…" favoritesConfig={{ type: "pipe", category: "🔩 Flange Bolts", makeId: (row) => `flange-${row[0]}-${row[1]}`, makeName: (row) => `${row[0]} ${row[1]} Flange`, makeDetail: (row) => `${row[2]}× ${row[3]} bolts · BC ${row[4]}" · OD ${row[5]}"` }} />}
            {flangeView === "gaskets" && <DataTable headers={gasketHeaders} rows={gasketRows} searchable searchPlaceholder="Search gaskets…" favoritesConfig={{ type: "pipe", category: "🔩 Gaskets", makeId: (row) => `gasket-${row[0]}-${row[1]}-${row[2]}`, makeName: (row) => `${row[0]} ${row[1]} ${row[2]}`, makeDetail: (row) => `ID ${row[3]}" · OD ${row[4]}" · ${row[6]}` }} />}
            {flangeView === "face" && <DataTable headers={flangeFaceHeaders} rows={flangeFaceRows} searchable searchPlaceholder="Search face types…" favoritesConfig={{ type: "pipe", category: "🔩 Flange Face", makeId: (row) => `face-${row[1]}-${row[4]}`, makeName: (row) => `${row[0]} (${row[1]})`, makeDetail: (row) => `${row[2]} · ${row[4]} · ${row[3]} µin` }} />}
          </div>
        </div>
      );
      case "torque": return (
        <div>
          <div className="flex gap-1 bg-secondary/50 rounded-lg p-0.5 mb-3">
            {(["flange", "bolt"] as const).map(v => (
              <button key={v} onClick={() => { lightTap(); setTorqueView(v); }}
                className={`flex-1 py-1.5 px-2 rounded-md text-xs font-medium transition-all ${torqueView === v ? "bg-card text-foreground shadow-sm border border-border" : "text-muted-foreground"}`}>
                {v === "flange" ? "Flange Torque Calc" : "Bolt Torque Table"}
              </button>
            ))}
          </div>
          {torqueView === "flange" ? <FlangeTorqueCalculator /> : (
            <div className="-mx-4"><DataTable headers={boltTorqueHeaders} rows={boltTorqueRows} searchable searchPlaceholder="Search bolt torque specs…" favoritesConfig={{ type: "pipe", category: "🔩 Bolt Torque", makeId: (row) => `bolttorque-${row[0]}-${row[1]}`, makeName: (row) => `${row[0]} ${row[1]}`, makeDetail: (row) => `Torque ${row[3]} ft-lb · Lubed ${row[4]} ft-lb` }} /></div>
          )}
        </div>
      );
      case "valvecv": return <div className="-mx-4"><DataTable headers={valveCvHeaders} rows={valveCvRows} searchable searchPlaceholder="Search valve Cv…" favoritesConfig={{ type: "pipe", category: "🔧 Valve Cv", makeId: (row) => `valvecv-${row[0]}-${row[1]}-${row[3]}`, makeName: (row) => `${row[0]} ${row[1]} (${row[3]})`, makeDetail: (row) => `Cv ${row[2]} · ΔP ${row[4]} psi @10 GPM` }} /></div>;
      case "offset": return <OffsetCalculator />;
      case "rolling": return <RollingOffsetCalculator />;
      case "friction": return <FrictionLossCalculator />;
      case "eqlength": return <EquivalentLengthCalculator />;
      case "gpmsizing": return <GpmSizingCalculator />;
      case "maxgpm": return <MaxGpmChart />;
      case "pressuredrop": return <PressureDropCalculator />;
      case "steamsizing": return <SteamPipeSizingCalculator />;
      case "gassizing": return <GasPipeSizingCalculator />;
      case "threadref": return <PipeThreadReference />;
      case "threadtap": return <div className="-mx-4"><DataTable headers={threadTapHeaders} rows={threadTapRows} searchable searchPlaceholder="Search threads & tap drills…" favoritesConfig={{ type: "pipe", category: "🔧 Thread & Tap", makeId: (row) => `threadtap-${row[0]}-${row[1]}`, makeName: (row) => `${row[0]}`, makeDetail: (row) => `${row[1]} · TPI ${row[2]} · ${row[3]}` }} /></div>;
      case "steam": return <div className="-mx-4"><DataTable headers={steamTableHeaders} rows={steamTableRows} searchable searchPlaceholder="Search steam table…" /></div>;
      case "fixtures": return <div className="-mx-4"><DataTable headers={fixtureUnitHeaders} rows={fixtureUnitRows} searchable searchPlaceholder="Search fixtures…" favoritesConfig={{ type: "pipe", category: "🚿 Fixture Units", makeId: (row) => `fixture-${row[0]}`, makeName: (row) => `${row[0]}`, makeDetail: (row) => `DFU ${row[1]} · WSFU ${row[6]} · Trap ${row[3]}` }} /></div>;
      case "drains": return <DrainSizingCalculator />;
      case "vents": return <VentSizingCalculator />;
      case "supply": return <WaterSupplySizingCalculator />;
      case "draintbl": return <div className="-mx-4"><DataTable headers={drainSizingHeaders} rows={drainSizingRows} searchable searchPlaceholder="Search drain sizes…" favoritesConfig={{ type: "pipe", category: "🚿 Drain Sizing", makeId: (row) => `drain-${row[0]}`, makeName: (row) => `Drain ${row[0]}`, makeDetail: (row) => `1/4" slope: ${row[3]} DFU` }} /></div>;
      case "venttbl": return <div className="-mx-4"><DataTable headers={ventSizingHeaders} rows={ventSizingRows} searchable searchPlaceholder="Search vent sizes…" favoritesConfig={{ type: "pipe", category: "🚿 Vent Sizing", makeId: (row) => `vent-${row[0]}`, makeName: (row) => `Vent ${row[0]}`, makeDetail: (row) => `50ft: ${row[2]} DFU · 100ft: ${row[3]} DFU` }} /></div>;
      case "expansion": return <PipeThermalExpansion />;
      case "insulation": return <PipeInsulationCalc />;
      case "pipeweight": return <PipeWeightCalculator />;
      case "hangerload": return <HangerLoadCalculator />;
      case "trapeze": return <TrapezeHangerCalculator />;
      case "grooveweld": return <div className="-mx-4"><DataTable headers={grooveWeldHeaders} rows={grooveWeldRows} searchable searchPlaceholder="Search groove & weld prep…" favoritesConfig={{ type: "pipe", category: "🔧 Groove & Weld", makeId: (row) => `gw-${row[0]}-${row[1]}`, makeName: (row) => `${row[1]} ${row[0]}`, makeDetail: (row) => `${row[2]} · ${row[3]}` }} /></div>;
      case "fdi": return <FactoredWeldInchCalc />;
      case "formulas": return <TradeFormulas trade="pipe" />;
      default: return null;
    }
  };

  return (
    <TradeToolShell
      title="🔧 Pipe"
      subtitle="Sizing, fittings, plumbing code, offsets & reference"
      categories={PIPE_CATEGORIES}
      aliases={TOOL_ALIASES}
      renderTool={renderTool}
    />
  );
};

export default PipeSizing;
